import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSafetyNumber } from '@/hooks/useSafetyNumber';
import { formatSafetyNumber } from '@/lib/safety-number';

interface SafetyNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  otherUserId: string | null;
  currentUserId: string | null;
  otherUserName: string;
}

export function SafetyNumberModal({
  isOpen,
  onClose,
  otherUserId,
  currentUserId,
  otherUserName,
}: SafetyNumberModalProps) {
  const { safetyNumber, isLoading, verifiedContact, keyChanged, markVerified } = useSafetyNumber(otherUserId, currentUserId);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const groups = safetyNumber ? formatSafetyNumber(safetyNumber) : [];

  const handleCopy = async () => {
    if (!safetyNumber) return;
    await navigator.clipboard.writeText(groups.join(' '));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async () => {
    setVerifying(true);
    await markVerified();
    setVerifying(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[61] max-w-sm mx-auto bg-card border border-border/40 rounded-2xl shadow-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
                <h3 className="text-base font-semibold">Verify Security</h3>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Compare these numbers with {otherUserName} to verify end-to-end encryption.
            </p>

            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : safetyNumber ? (
              <>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {groups.map((group, i) => (
                    <div
                      key={i}
                      className="bg-muted/50 rounded-lg px-2 py-1.5 text-center font-mono text-sm tracking-wider"
                    >
                      {group}
                    </div>
                  ))}
                </div>

                {keyChanged && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3">
                    <p className="text-xs text-amber-400 font-medium">
                      ⚠️ Security code changed since last verification
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1.5"
                    onClick={handleCopy}
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                  {!verifiedContact || keyChanged ? (
                    <Button
                      size="sm"
                      className="flex-1 gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleVerify}
                      disabled={verifying}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {verifying ? 'Verifying…' : 'Mark Verified'}
                    </Button>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-xs text-emerald-400 font-medium gap-1">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Verified
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Encryption keys not yet exchanged.
              </p>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
